import { type RankListResult } from 'components/panels/eventsSplitDraws/selectedEvent/utils';
import { client } from 'config/graphql/client';
import { DEMO_MODE_INFO_MESSAGE } from 'config/graphql/errors';
import { GET_RANKINGS_AND_RATINGS, GET_WTN_RATING } from 'config/graphql/queries';
import { GET_PT_POINTS, GET_RANKING_LIST, GET_RANKINGS_LIST } from 'config/graphql/rankingQueries';
import { rankingsCilent } from 'config/graphql/rankingsClient';
import { CloudError } from 'repository/CloudError';
import type Repository from 'repository/repository';
import { type CloudResult, type RepositoryCacheType } from 'repository/repository';
import { type PtResult } from 'typedefs/playTrackerTypes';
import { type RankingsAndRatings, type RankListFilter } from 'typedefs/rankingsAndRatings';
import { type WtnResult } from 'typedefs/wtnTypes';
import { isStandalone } from 'utils/environment';

const rankingsClient = rankingsCilent();

export interface RankingAndRatingsRepository extends Repository<RankingsAndRatings> {
  getRankingList(
    id: string,
    itemPageArgs: { skip: number; limit: number },
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<any>>;
  getRankingsList(ranklistFilter: RankListFilter, cache?: RepositoryCacheType): Promise<CloudResult<any>>;
  getWtnRating(nationalIds: string[], cache?: RepositoryCacheType): Promise<CloudResult<WtnResult>>;
  getPtPoints(nationalIds: any[], cache?: RepositoryCacheType): Promise<CloudResult<PtResult>>;
}

export class RankingsAndRatingsRepositoryImpl implements RankingAndRatingsRepository {
  async get(id: string, cache?: RepositoryCacheType): Promise<CloudResult<any>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await client.query<any>({
      query: GET_RANKINGS_AND_RATINGS,
      fetchPolicy: cache,
      variables: { csTournamentEventId: id },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  save(item: any) {
    console.log(item);
    return Promise.resolve(undefined);
  }

  async getRankingList(
    id: string,
    itemPageArgs: { skip: number; limit: number },
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<any>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await rankingsClient.query<any>({
      query: GET_RANKING_LIST,
      fetchPolicy: cache,
      variables: { id, itemPageArgs },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getRankingsList(
    ranklistFilter: RankListFilter,
    cache?: RepositoryCacheType,
  ): Promise<CloudResult<RankListResult>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await rankingsClient.query<any>({
      query: GET_RANKINGS_LIST,
      fetchPolicy: cache,
      variables: { ranklistFilter },
    });
    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getWtnRating(nationalIds: string[], cache?: RepositoryCacheType): Promise<CloudResult<WtnResult>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const graphqlResult = await client.query<WtnResult>({
      query: GET_WTN_RATING,
      fetchPolicy: cache,
      variables: { nationalIds },
    });

    if (graphqlResult?.error) {
      return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
    }

    return Promise.resolve({ data: graphqlResult.data });
  }

  async getPtPoints(nationalIds: string[], cache?: RepositoryCacheType): Promise<CloudResult<PtResult>> {
    if (isStandalone()) {
      return Promise.resolve({ error: new CloudError('demo-mode-query', DEMO_MODE_INFO_MESSAGE) });
    }

    const personIds = nationalIds?.map((id: string) => ({ identifier: id, type: 'UAID' }));

    try {
      const graphqlResult = await rankingsClient.query<PtResult>({
        query: GET_PT_POINTS,
        fetchPolicy: cache,
        variables: { personIds },
      });

      if (graphqlResult?.error) {
        return Promise.resolve({ error: new CloudError(graphqlResult?.error.name, graphqlResult?.error.message) });
      }

      return Promise.resolve({ data: graphqlResult.data });
    } catch (error: any) {
      return Promise.resolve({ error: new CloudError(error.name, error.message) });
    }
  }
}
